/**
 * CAT.1 §8.1 — category → default print location ids.
 * Placement ids match enterprise settings.placements keys:
 * 1 Front, 2 Back, 3 Left Chest, 4 Right Chest, 6 Right Sleeve, 7 Left Sleeve.
 *
 * Apparel masters we promote use Front / Back / Left Chest / both sleeves —
 * not Right Chest. When adding from a master, `masterPlacementIds` overrides
 * this fallback so the preset matches that style's real print locations.
 */

export const LOCATION_PRESET_GROUPS = {
  headwear: ["1", "15", "16"],
  apparel: ["1", "2", "3", "6", "7"],
  drinkware: ["1", "2"],
};

const HEADWEAR_RE =
  /\b(hats?|caps?|beanie|visor|headwear|trucker|dad hat|snapback)\b/i;
const DRINKWARE_RE =
  /\b(mug|tumbler|bottle|drinkware|cup|can cooler|glass)\b/i;
const APPAREL_RE =
  /\b(tee|t-?shirt|apparel|hoodie|sweat|polo|jacket|tank|shirt|garment)\b/i;

function textBlob(productSignals = {}) {
  const cats = (productSignals.categories || productSignals.productCategories || [])
    .map((c) => (typeof c === "string" ? c : c?.name || c?.label || ""))
    .join(" ");
  return [
    productSignals.name,
    productSignals.productName,
    productSignals.style,
    productSignals.brand,
    productSignals.category,
    productSignals.shippingCategory,
    cats,
    productSignals.presetKey,
  ]
    .filter(Boolean)
    .join(" ");
}

export function normalizePlacementIds(ids = []) {
  return [...new Set((ids || []).map((id) => String(id)).filter(Boolean))].sort(
    (a, b) => Number(a) - Number(b) || a.localeCompare(b),
  );
}

/** Placement chips a master actually ships — placements map, else print areas. */
export function placementIdsFromMasterSources({
  placements,
  placementDetails,
} = {}) {
  const fromPlacements = Object.keys(placements || {});
  if (fromPlacements.length) return normalizePlacementIds(fromPlacements);
  return normalizePlacementIds(Object.keys(placementDetails?.default || {}));
}

/**
 * Infer which LOCATION_PRESET_GROUPS key applies to a product.
 * @returns {"headwear"|"apparel"|"drinkware"|"catalog"|null}
 */
export function inferLocationPresetKey(productSignals = {}) {
  if (normalizePlacementIds(productSignals.masterPlacementIds).length) {
    return "catalog";
  }
  if (productSignals.presetKey && LOCATION_PRESET_GROUPS[productSignals.presetKey]) {
    return productSignals.presetKey;
  }
  const blob = textBlob(productSignals);
  if (!blob.trim()) return null;
  if (HEADWEAR_RE.test(blob)) return "headwear";
  if (DRINKWARE_RE.test(blob)) return "drinkware";
  if (APPAREL_RE.test(blob)) return "apparel";
  return null;
}

/** Default location ids for a product (falls back to apparel). */
export function defaultLocationIdsForProduct(productSignals = {}) {
  const masterIds = normalizePlacementIds(productSignals.masterPlacementIds);
  const extra = normalizePlacementIds(productSignals.extraPlacementIds);
  if (masterIds.length) return normalizePlacementIds([...masterIds, ...extra]);
  const key = inferLocationPresetKey(productSignals) || "apparel";
  const resolved = LOCATION_PRESET_GROUPS[key] ? key : "apparel";
  const base = [
    ...(LOCATION_PRESET_GROUPS[resolved] || LOCATION_PRESET_GROUPS.apparel),
  ];
  return normalizePlacementIds([...base, ...extra]);
}

/** Build printingPrice map from location id list. */
export function buildPrintingPriceFromIds(ids = []) {
  return (ids || []).reduce((acc, id) => {
    const key = String(id);
    acc[key] = { printingPrice: "0", upcharge: "" };
    return acc;
  }, {});
}
